/**
 * Takes a patient payment on a Tyro EFTPOS terminal, against the Xero invoice a lodged claim already raised.
 *
 * This is the other half of the claims screen's payment step. The manual form beside it *records* a payment
 * somebody already took - cash, a bank transfer, a card run on a standalone machine - whereas this one *takes*
 * it: the charge is sent to a Tyro terminal, the cardholder approves it there, and the payment is posted onto
 * the invoice from Tyro's answer rather than from what an admin typed.
 *
 * The two amounts it collects are the two the patient can owe on an ECLIPSE claim:
 *   - the gap on an agreement (AG) or scheme (SC) claim, where the funder settles its benefit to the practice
 *     and the patient owes the difference;
 *   - the whole charge on a patient (PC) claim, where the funder rebates the patient directly and so nothing
 *     it assesses ever reaches this invoice.
 * Which of those it is, and what it comes to, is worked out by the host - see claimModalController's
 * getPatientAmountDue(), where the funder's share is known - and arrives here as a figure.
 *
 * Nothing here pairs, addresses or selects a terminal. The SDK lists the practice's registered Tyro machines,
 * sends the purchase to whichever the operator picks and reports the cardholder's answer back, so a terminal
 * is reached with no cable, adapter or Bluetooth pairing and several workstations can share one machine.
 */
app.component('tyroTerminalPayment', {
    templateUrl: 'app/src/js/components/tyroTerminalPayment/tyro-terminal-payment.html',
    controller: 'tyroTerminalPaymentController',
    bindings: {
        patient: '<',
        providerId: '<',
        // The lodged claim this payment settles. Its invoice_id is what the payment is posted against server
        // side, so a claim that has not raised an invoice yet cannot be paid here.
        claim: '<',
        // What the patient owes, in dollars. Nil or missing is treated as nothing to collect rather than as a
        // terminal charge of zero, which Tyro would refuse.
        amount: '<',
        // The practice's Xero accounts. The transaction row needs a bank account to pay into and a revenue
        // account to post items against, the same two the claim flow picks.
        accounts: '<',
        onComplete: '&'
    }
});

app.controller('tyroTerminalPaymentController', [
    '$window', '$timeout', 'TyroAPI', 'ENV', 'ExceptionAPI',
    function ($window, $timeout, TyroAPI, ENV, ExceptionAPI) {

        var $ctrl = this;

        $ctrl.isLoading = true;
        $ctrl.errorMessage = null;
        // Set once the charge has been approved on the terminal, so the view stops offering a payment that has
        // already been taken while the host finishes reloading behind it.
        $ctrl.paymentTaken = false;

        // The CatTrax transaction row this payment is recorded as. Created before the SDK renders, because the
        // webhooks the SDK is given have to name it: it is the row Tyro's answer is applied to, and the row
        // that carries the invoice the payment is posted onto.
        var transaction = null;

        // Set when the host drops this component (Back, Payment Options) so a request still in flight
        // does not go on to raise a transaction or open Tyro's overlay over a view that has gone.
        var destroyed = false;
        var renderTimer = null;

        $ctrl.$onDestroy = function () {
            destroyed = true;
            if (renderTimer) {
                $timeout.cancel(renderTimer);
                renderTimer = null;
            }
        };

        $ctrl.$onInit = function () {
            var problem = validate();
            if (problem) {
                $ctrl.errorMessage = problem;
                $ctrl.isLoading = false;
                return;
            }

            TyroAPI.getAccessToken().then(function (response) {
                if (destroyed) {
                    return;
                }
                return createTransaction(response.data.access_token);
            }).catch(function (error) {
                // Status and message only. A rejected payment request carries the request back with it, and
                // that request is the patient and what they are being charged.
                $ctrl.errorMessage = ExceptionAPI.serverMessage(error)
                    || "Unable to start a terminal payment. Please try again, or record the payment manually.";
            }).finally(function () {
                $ctrl.isLoading = false;
            });
        };

        /**
         * Why this payment cannot be taken, in the words the operator needs to act on it, or null where it can.
         *
         * Checked here rather than left to the server because each of these has a different thing to do about
         * it - connect Xero, wait for the funder, use the manual form - and a 400 from the transaction endpoint
         * would say none of them.
         */
        function validate() {
            if (!$ctrl.claim || !$ctrl.claim.invoice_id) {
                return "This claim has no invoice to take a payment against yet.";
            }
            if (!($ctrl.amount > 0)) {
                return "There is nothing outstanding for the patient to pay on this claim.";
            }
            if (!paymentAccountId() || !itemsAccountId()) {
                return "A bank account and a revenue account are needed before a payment can be taken. "
                    + "Check the practice's accounting connection in settings.";
            }
            return null;
        }

        function paymentAccountId() {
            var account = ($ctrl.accounts || []).find(function (candidate) {
                return candidate.account_class === 'BANK';
            });
            return account ? account.account_id : null;
        }

        function itemsAccountId() {
            var account = ($ctrl.accounts || []).find(function (candidate) {
                return candidate.account_class !== 'BANK';
            });
            return account ? account.account_id : null;
        }

        /**
         * Records the payment as its own PATIENT_PAYMENT transaction rather than reusing the claim's row.
         *
         * The claim's row belongs to the funder side of the account: its status is the funder's assessment and
         * its benefit figures are what the funder allowed. A card payment taken off the patient is a separate
         * financial event with its own outcome, and writing it over the claim would lose one or the other. The
         * two are tied together by source_transaction_id and by both naming the same invoice.
         */
        function createTransaction(accessToken) {
            return TyroAPI.createTransaction({
                patient_id: $ctrl.patient.id,
                account_payment_id: paymentAccountId(),
                account_items_id: itemsAccountId(),
                transaction_type: 'PATIENT_PAYMENT',
                // The invoice the payment is posted onto once Tyro says it was approved.
                invoice_id: $ctrl.claim.invoice_id,
                charge_amount: roundToCents($ctrl.amount),
                // The claim the money is owed on, so a payment can be read back to what it settled.
                source_transaction_id: $ctrl.claim.transaction_id
            }).then(function (response) {
                transaction = response.data;
                if (destroyed) {
                    return;
                }
                render(accessToken, transaction);
            });
        }

        function render(accessToken, paymentTransaction) {
            // Deferred a digest before handing over to the SDK, which renders its own overlay outside Angular
            // - the same thing the claim flow's directive does, and for the same reason: rendering inside the
            // digest that resolved the transaction request leaves the SDK racing it.
            renderTimer = $timeout(function () {
                renderTimer = null;
                if (destroyed) {
                    return;
                }
                $window.MedipassTransactionSDK.setConfig({
                    // Production only on a production domain; dev and localhost stay on staging, so a test
                    // payment can never reach a live card.
                    env: ENV.MEDIPASS_ENV,
                    appVersion: "0.0.0",
                    token: accessToken,
                    appId: "cattrax-web"
                });

                $window.MedipassTransactionSDK.renderCreateTransaction(
                    {
                        // The patient-payment platform, as against the 'funder' platform the claim flow uses.
                        // Nothing here is claimed off anybody - it is a card purchase.
                        platform: 'virtual-terminal',
                        // Sends the charge to a Tyro EFTPOS machine for the cardholder to approve, rather than
                        // to a keyed card, an SMS or a payment link.
                        paymentMethod: 'terminal',
                        providerNumber: asText($ctrl.providerId),
                        // Tyro groups transactions sharing a reference into one invoice, so the Xero invoice id
                        // is the right thing to key it on: a part payment taken today and the rest taken
                        // tomorrow belong to the same invoice and should read as two payments against it.
                        invoiceReference: paymentTransaction.invoice_id || $ctrl.claim.invoice_id,
                        // Money reaches the SDK as a formatted string - see the claim flow's claimableItems.
                        chargeAmount: '$' + roundToCents($ctrl.amount).toFixed(2),
                        patient: patientDetails()
                    },
                    {
                        allowEdit: false,
                        version: "3",
                        webhooks: paymentWebhooks(paymentTransaction),
                        // Through $timeout: the SDK's callbacks fire outside Angular, so a flag set straight
                        // from one never reaches a digest and the panel below would not change.
                        onSuccess: function () {
                            // Set straight away, so an onCloseModal fired in the same tick still reads
                            // it; the $timeout is only there to run a digest.
                            $ctrl.paymentTaken = true;
                            $timeout(angular.noop);
                        },
                        onError: function (error) {
                            // The message only. The SDK hands back the request it failed on, patient included.
                            console.error('Terminal payment error:', error && error.message);
                        },
                        // The one callback that fires however the SDK is left; onCancel does not always run.
                        onCloseModal: function (result) {
                            finish(result ? result.status : null);
                        }
                    });
            }, 0);
        }

        /**
         * The two events that say a card payment landed, plus the one that says the invoice was cancelled.
         *
         * The claim flow's events are all about what a funder decided and none of them fire here. A terminal
         * purchase reports paymentApproved when the cardholder approves the charge and invoiceCompleted when
         * that clears the invoice's balance; the payment is posted off the first, and the second is what
         * confirms nothing is left owing. Tyro does not guarantee event order, so the server treats both as
         * idempotent rather than assuming which arrives first.
         */
        function paymentWebhooks(paymentTransaction) {
            var url = webhookUrl(paymentTransaction);
            return ['paymentApproved', 'invoiceCompleted', 'invoiceCancelled'].map(function (event) {
                return {
                    url: url,
                    event: event,
                    method: 'POST',
                    headers: { "X-Session-Key": paymentTransaction.session_key }
                };
            });
        }

        /**
         * The absolute URL Tyro posts events back to.
         *
         * ENV.API is a path relative to the served root everywhere but localhost, where it is a full URL to the
         * dev API - so it is only prefixed with the origin when it needs to be. A localhost webhook cannot be
         * delivered either way, which is why a terminal payment taken locally is only ever confirmed by the
         * SDK's own callback.
         */
        function webhookUrl(paymentTransaction) {
            var base = /^https?:\/\//.test(ENV.API) ? ENV.API : $window.location.origin + '/' + ENV.API;
            return base + '/api/tyro/webhooks/patient/' + paymentTransaction.patient_id
                + '/transaction/' + paymentTransaction.transaction_id;
        }

        function patientDetails() {
            var details = {
                refId: $ctrl.patient.id.toString(),
                firstName: asText($ctrl.patient.firstName),
                lastName: asText($ctrl.patient.lastName),
                dob: asText($ctrl.patient.dateOfBirth)
            };
            // Only where the record holds one. Tyro uses it to text the cardholder their receipt, and an empty
            // string reads to the SDK as a mobile it should try to send to.
            var mobile = asText($ctrl.patient.mobileNumber);
            if (mobile) {
                details.mobile = mobile;
            }
            return details;
        }

        // The statuses that mean a charge actually went through. Anything else - cancelled, errored, or the
        // window simply closed - left the terminal without taking money.
        const PAID_STATUSES = ['approved', 'completed', 'paid'];

        /**
         * Hands the outcome back to the host.
         *
         * paymentTaken is the part that matters: the host waits on Tyro's webhook to post the money, and there is
         * nothing to wait for when nobody was charged. It is true when the SDK reported success, and also when it
         * closed on a status that means the charge went through - onSuccess is the reliable signal, the status is
         * the belt to its braces.
         */
        function finish(status) {
            const paid = $ctrl.paymentTaken
                || PAID_STATUSES.indexOf(String(status).toLowerCase()) !== -1;

            $timeout(function () {
                if ($ctrl.onComplete) {
                    $ctrl.onComplete({
                        status: status,
                        payload: transaction,
                        paymentTaken: paid
                    });
                }
            });
        }

        function asText(value) {
            return (value === null || value === undefined) ? '' : String(value).trim();
        }

        function roundToCents(amount) {
            return Math.round(amount * 100) / 100;
        }
    }
]);
